import { useAppSelector } from '@/app/store';

import {
  selectAuthStatus,
  selectCurrentUser,
  selectIsAuthenticated,
  selectIsSessionResolved,
  hasRole,
} from '../store/authSlice';
import type { AuthStatus, User, UserRole } from '../types/auth.types';

export interface UseAuthResult {
  user: User | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  /** False while the initial session probe is still in flight. */
  isSessionResolved: boolean;
  /** UX-level role check — see the note in `authSlice`. */
  can: (allowed: readonly UserRole[]) => boolean;
}

/**
 * Reads the current session.
 *
 * Selectors are called individually rather than returning one object from a
 * single selector, so a component only rerenders when a field it actually
 * reads changes.
 */
export function useAuth(): UseAuthResult {
  const user = useAppSelector(selectCurrentUser);
  const status = useAppSelector(selectAuthStatus);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isSessionResolved = useAppSelector(selectIsSessionResolved);

  return {
    user,
    status,
    isAuthenticated,
    isSessionResolved,
    can: (allowed) => hasRole(user, allowed),
  };
}
